import { DependencyList, useRef, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

interface IOptions {
    debounce?: boolean;
}

interface IReturnValue<TResult> {
    data: TResult | undefined;
    loading: boolean;
    makeFetch: () => void;
}

export const useFetch = <TResult>(
    asyncCallback: () => Promise<TResult> | TResult | null | undefined,
    deps: DependencyList,
    { debounce }: IOptions = {}
): IReturnValue<TResult> => {
    const debouncedCallback = useRef(
        _.debounce((deferResolve, cb) => {
            deferResolve(cb());
        }, 800)
    );

    const [data, setData] = useState<TResult>();
    const [loading, setLoading] = useState(false);

    const callback = useMemo(
        () => {
            if (!debounce) return asyncCallback;

            return () =>
                new Promise(resolve => {
                    debouncedCallback.current?.(resolve, asyncCallback);
                }) as Promise<TResult>;

            // Actually it's okay for such custom hook
            // eslint-disable-next-line
        },
        deps || [debouncedCallback, asyncCallback]
    );

    const makeFetch = useCallback(async () => {
        setLoading(true);
        const result = await callback();
        result && setData(result);

        setLoading(false);
    }, [callback]);

    useEffect(() => {
        makeFetch();
    }, [makeFetch]);

    return { data, loading, makeFetch };
};
